/**
 * Where the login page may send the browser after a sign-in.
 *
 * `?returnTo=` is written by the auth gate in App.tsx, but it is read from the
 * query string, and anyone can craft the link. The value is parsed the way a
 * browser parses it and kept only when it is a page of this origin — never
 * checked by its first characters: in a web address `\` is a `/` and tabs and
 * newlines are dropped, so `/\evil.example` and `/<tab>/evil.example` both
 * "start with `/`" and both name another host. The twin of the backend's
 * `safeReturnTo` (auth-sso.ts), which decides the redirect after an SSO login;
 * this one decides the `navigate()` after a local login.
 *
 * The router hands the string to the history API. `replaceState` throws on a
 * cross-origin URL (the login succeeded and the page said "Login failed");
 * `pushState` is wrapped and falls back to `window.location.assign`, which
 * would leave the site. Neither is allowed to happen.
 *
 * Refused as well:
 * - a path under `/api/` — not a page, and an uploaded file is served there;
 * - `/auth/login` and `/auth/setup` — AuthedApp renders those screens for the
 *   exact pathname with no user check, so a signed-in user would land on a
 *   login form again or on a setup wizard whose POST the backend refuses.
 *
 * Returns the parsed path (`pathname + search + hash`), never the raw string,
 * or `/`.
 */
export function safeReturnTo(
  raw: string | null | undefined,
  origin: string = window.location.origin,
): string {
  if (!raw) return '/';
  let base: URL;
  let url: URL;
  try {
    base = new URL(origin);
    url = new URL(raw, base);
  } catch {
    return '/';
  }
  if (base.protocol !== 'https:' && base.protocol !== 'http:') return '/';
  if (url.origin !== base.origin) return '/';
  // The router decodes a path before it matches it, so `/%61pi/` is `/api/`.
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(url.pathname).toLowerCase();
  } catch {
    return '/';
  }
  if (decodedPath === '/api' || decodedPath.startsWith('/api/')) return '/';
  if (decodedPath === '/auth/login' || decodedPath === '/auth/setup') return '/';
  const target = url.pathname + url.search + url.hash;
  // `/.//evil.example` is on this origin, but its path is `//evil.example`,
  // which handed to the router on its own names a host. What is navigated to
  // must read as the URL that was checked.
  if (new URL(target, base).href !== url.href) return '/';
  return target;
}
